import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { titleCase } from '../../lib/format'
import type { Organization } from '../../types'
import { Card, EmptyState, Skeleton, Alert, Badge } from '../../components/ui'
import { CheckIcon } from '../../components/icons'

/* Faithful port of anansi/app/templates/admin/compliance_queue.html + its
 * view admin/routes.py:compliance_queue(). Gates whether a newly
 * self-registered company/cooperative may operate (register sites, log
 * production, dispatch loads) - a maker-checker queue distinct from the
 * generic approvals queue (Group D) and from the statutory Role Catalog. */

const STATUS_CHIPS: { value: string; label: string }[] = [
  { value: 'pending_approval', label: 'Awaiting approval' },
  { value: 'changes_requested', label: 'Changes requested' },
  { value: 'pending_setup', label: 'Still setting up' },
  { value: 'incomplete_governance', label: 'Incomplete governance' },
  { value: 'approved', label: 'Approved' },
]

type Tone = 'success' | 'danger' | 'warning' | 'neutral'

function statusTone(status: string): Tone {
  if (status === 'approved') return 'success'
  if (status === 'changes_requested') return 'danger'
  if (status === 'incomplete_governance') return 'warning'
  return 'warning' // pending_setup / pending_approval
}

interface ComplianceResponse {
  ok: boolean
  items: Organization[]
  counts: Record<string, number>
  status: string
}

export default function AdminCompliance() {
  const [status, setStatus] = useState('pending_approval')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'compliance', status],
    queryFn: () => api.get<ComplianceResponse>(`/admin/compliance?status=${status}`),
  })

  const rows = data?.items ?? []
  const counts = data?.counts ?? {}

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compliance setup</h1>
          <p className="page-head__sub">
            Organisations must complete a compliance checklist and be approved here before they
            may register sites, log production or dispatch loads.
          </p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-4)' }}>
        {STATUS_CHIPS.map((c) => (
          <button
            key={c.value}
            className={`btn btn--sm ${status === c.value ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setStatus(c.value)}
          >
            {c.label}
            {counts[c.value] ? <span style={{ marginLeft: 4, opacity: 0.75 }}>{counts[c.value]}</span> : null}
          </button>
        ))}
      </div>

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="stack"><Skeleton h={60} /><Skeleton h={60} /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<CheckIcon size={22} />} title="Nothing in this list right now" body="" />
      ) : (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Organisation</th><th>Type</th><th>Region</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const st = o.registration_status || 'pending_setup'
                  return (
                    <tr key={o.id}>
                      <td style={{ fontWeight: 550 }}>{o.name}</td>
                      <td>{titleCase(o.type)}</td>
                      <td>{titleCase(o.region_id) || '—'}</td>
                      <td><Badge tone={statusTone(st)}>{titleCase(st)}</Badge></td>
                      <td style={{ textAlign: 'right' }}>
                        <Link to={`/compliance/${o.id}`}>Review →</Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  )
}
